import APIServices from './APIServices'
import {
  AreasInterface,
  StockAreaProducts,
} from '../interfaces/ServerInterfaces'

// Cache for stock areas
let stockAreasCache: AreasInterface[] = []
let sharedAreasCache: AreasInterface[] = []
let areasLoaded = false

// Cache for products by area (Map to store products for each area)
const productsByAreaCache = new Map<string | number, StockAreaProducts[]>()

export const getStockAreas = async (params: {
  page?: number
  per_page?: number
  search?: string
}) => {
  const hasSearch = params?.search && String(params.search).trim() !== ''
  if (!hasSearch && areasLoaded && stockAreasCache.length > 0) {
    return stockAreasCache
  }

  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => value != null)
  )
  const finalParams = { ...cleanParams, type: 'STOCK', all_data: true }

  const response = await APIServices.get('/administration/area', {
    params: finalParams,
  })

  const items = response.data?.items || []

  if (!hasSearch) {
    stockAreasCache = items
    areasLoaded = true
  }

  return items
}

/**
 * Get stock areas for dispatch with persistent cache
 * This cache persists even when modals are closed and reopened
 * Uses per_page=20 for pagination as requested
 */
export const getStockAreasForDispatch = async (): Promise<{
  stockAreas: AreasInterface[]
  sharedAreas: AreasInterface[]
}> => {
  // Return cached data if already loaded
  if (areasLoaded) {
    return {
      stockAreas: stockAreasCache,
      sharedAreas: sharedAreasCache,
    }
  }

  // Fetch both endpoints in parallel with per_page=20
  const [stockAreasResponse, sharedAreasResponse] = await Promise.all([
    APIServices.get(
      '/administration/area?per_page=20&type=STOCK&all_data=true'
    ),
    APIServices.get('/administration/sharedarea'),
  ])

  // Store in cache
  stockAreasCache = stockAreasResponse.data?.items || []
  sharedAreasCache = sharedAreasResponse.data?.items || []
  areasLoaded = true

  return {
    stockAreas: stockAreasCache,
    sharedAreas: sharedAreasCache,
  }
}

export const getAreas = async (params: {
  page?: number
  per_page?: number
  search?: string
  type?: string
}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => value != null)
  )

  const response = await APIServices.get('/administration/area', {
    params: cleanParams,
  })

  return response.data?.items || []
}

export const getSharedAreas = async () => {
  const response = await APIServices.get('/administration/sharedarea')

  return response.data?.items || []
}

/**
 * Get products by area with persistent cache and pagination support
 * Cache persists even when modals are closed and reopened
 * Each area has its own cache entry
 */
export const getProductsByAreaCached = async (
  areaId: string | number,
  params?: {
    all_data?: boolean
    search?: string
    page?: number
    per_page?: number
  }
): Promise<{
  items: StockAreaProducts[]
  totalPages: number
  totalItems: number
  currentPage: number
}> => {
  const hasSearch = params?.search && String(params.search).trim() !== ''
  const isPaginated = params?.page !== undefined

  // Return cached data if no search, no pagination and cache exists for this area
  if (!hasSearch && !isPaginated && productsByAreaCache.has(areaId)) {
    const cached = productsByAreaCache.get(areaId)
    if (cached) {
      return {
        items: cached,
        totalPages: 1,
        totalItems: cached.length,
        currentPage: 1,
      }
    }
  }

  // Build query params
  const queryParams: Record<string, string | boolean | number> = {}
  if (params?.all_data && !isPaginated) {
    queryParams.all_data = true
  }
  if (hasSearch && params?.search) {
    queryParams.search = params.search
  }
  if (isPaginated) {
    queryParams.page = params.page || 1
    queryParams.per_page = params.per_page || 10
  }

  // Build query string
  const queryString = Object.keys(queryParams).length
    ? '?' +
      Object.entries(queryParams)
        .map(([key, value]) => `${key}=${value}`)
        .join('&')
    : ''

  // Fetch from API
  const response = await APIServices.get(
    `/administration/product/area/${areaId}${queryString}`
  )

  const products = response.data?.items || []
  const totalPages = response.data?.totalPages || 1
  const totalItems = response.data?.totalItems || products.length
  const currentPage = response.data?.currentPage || 1

  // Cache only if not searching and not paginated (all_data=true)
  if (!hasSearch && !isPaginated) {
    productsByAreaCache.set(areaId, products)
  }

  return {
    items: products,
    totalPages,
    totalItems,
    currentPage,
  }
}
